import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import "./App.scss";
import { Canvas } from "@react-three/fiber";
import { Html, OrbitControls, StatsGl } from "@react-three/drei";
// import { EffectComposer, N8AO } from "@react-three/postprocessing";
import * as THREE from "three";

import Main from "components/Main/Main";
import Scene from "components/Scene/Scene";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import Spinner from "components/Spinner/Spinner";
import BackgroundMusic from "components/Audio/BackgroundMusic";
import AudioDevPanel from "components/Audio/AudioDevPanel";
import PawsportModal from "components/PawsportModal/PawsportModal";
import ResourceModal from "components/ResourceModal/ResourceModal";
import CalendarModal from "components/CalendarModal/CalendarModal ";
import CreditsModal from "components/CreditsModal/CreditsModal";
import IntroSubtitle from "components/IntroSubtitle/IntroSubtitle";
import Quiz from "components/Quiz/Quiz";

import TitleImg from "assets/graphics/bearshares_logo.svg";
import BackBtnImg from "assets/graphics/back_01_v2.png";
import QuizBtnImg from "assets/graphics/quiz.png";
import LanguageBtnImg from "assets/graphics/language.png";
import PassportBtnImgEn from "assets/graphics/passport_en.png";
import PassportBtnImgFr from "assets/graphics/passport_fr.png";
import CreditsBtnImg from "assets/graphics/credits.png";
import SkipBtnImg from "assets/graphics/skip.png";

import data from "assets/data.json";
import i18n from "i18n/config";
import { useTranslation } from "react-i18next";
import ScrapbookModal from "components/ScrapbookModal/Scrapbook";
import MusicVideoModal from "components/MusicVideoModal/MusicVideoModal";

export let apiURL: string;

if (process.env.NODE_ENV === "development") {
  apiURL = "http://localhost:3001/api/";
} else {
  apiURL = window.location.origin + "/api/";
}

const params = new URLSearchParams(window.location.search);
const initialScene = params.get("scene") ? params.get("scene") : "";

const getAnswerText = (question: any, language: string) => {
  if (!question) return "";
  return language === "fr" ? question.answerText_fr : question.answerText_en;
};

const getAnswerParagraphs = (question: any, language: string) => {
  return getAnswerText(question, language)
    .split(/\n\s*\n/)
    .map((paragraph: string) => paragraph.trim())
    .filter(Boolean);
};

const getParagraphTimestamps = (question: any) => {
  if (!question) return [];

  if (Array.isArray(question.answerParagraphTimestamps)) {
    return question.answerParagraphTimestamps as number[];
  }

  return [];
};

const getTimedAnswerSegments = (question: any, language: string) => {
  const paragraphs = getAnswerParagraphs(question, language);
  const paragraphTimestamps = getParagraphTimestamps(question);

  if (paragraphTimestamps.length === 0 || paragraphs.length <= 1) {
    return paragraphs;
  }

  return paragraphs;
};

const hasTimedParagraphs = (question: any) => {
  return getParagraphTimestamps(question).length > 0;
};

const preventOrphan = (text: string) => text.replace(/\s(\S+)$/, "\u00A0$1");

function App() {
  const { t } = useTranslation();
  type BgmConfig = {
    src: string;
    volume?: number;
  };

  const hasUserInteractedRef = useRef(false);
  const skipNextPopstateRef = useRef(false);
  const bgmRef = useRef<HTMLAudioElement | null>(null);
  const [scene, setScene] = useState(initialScene);
  const [sceneLoaded, setSceneLoaded] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [visibleParagraphCount, setVisibleParagraphCount] = useState(0);

  const [isQuizMode, setIsQuizMode] = useState(false);
  const [isQuizNarrationPlaying, setIsQuizNarrationPlaying] = useState(false);
  const [quizCompletions, setQuizCompletions] = useState<{
    [key: string]: boolean;
  }>({});

  const [modalContent, setModalContent] = useState(null);

  const lang = i18n.language;

  // Set language based on path prefix
  useEffect(() => {
    const match = window.location.pathname.match(/^\/(en|fr)/);
    const lang = match ? match[1] : "en";
    i18n.changeLanguage(lang);
  }, []);

  // Warn users who try to use browser back button or reload the page
  useEffect(() => {
    window.history.replaceState(
      { ...(window.history.state || {}), __spiritBearGuard: "base" },
      "",
      window.location.href,
    );
    window.history.pushState(
      { ...(window.history.state || {}), __spiritBearGuard: "top" },
      "",
      window.location.href,
    );

    const handleBrowserBack = () => {
      if (skipNextPopstateRef.current) {
        skipNextPopstateRef.current = false;
        return;
      }

      setModalContent({ type: "browserBackWarning" });
      skipNextPopstateRef.current = true;
      window.history.go(1);
    };

    window.addEventListener("popstate", handleBrowserBack);

    return () => {
      window.removeEventListener("popstate", handleBrowserBack);
    };
  }, []);

  useEffect(() => {
    const markInteraction = () => {
      hasUserInteractedRef.current = true;
    };

    const handleReloadShortcut = (event: KeyboardEvent) => {
      const isReloadKey = event.key === "F5";
      const isReloadShortcut =
        (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r";

      if (isReloadKey || isReloadShortcut) {
        hasUserInteractedRef.current = true;
        event.preventDefault();
        window.alert(i18n.t("reload_warning"));
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUserInteractedRef.current) {
        return;
      }

      event.preventDefault();
      const warning = i18n.t("reload_warning");
      event.returnValue = warning;
      return warning;
    };

    document.addEventListener("pointerdown", markInteraction, {
      passive: true,
    });
    document.addEventListener("keydown", markInteraction);
    document.addEventListener("keydown", handleReloadShortcut);
    window.onbeforeunload = handleBeforeUnload;
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("pointerdown", markInteraction);
      document.removeEventListener("keydown", markInteraction);
      document.removeEventListener("keydown", handleReloadShortcut);
      window.onbeforeunload = null;
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // State to track the loading status of Suspense component
  const [suspenseLoading, setSuspenseLoading] = useState(true);

  // True while the main menu intro animation/audio is playing
  const [isIntroPlaying, setIsIntroPlaying] = useState(true);
  // True while the Classroom scene character intro is playing
  const [isClassroomIntroPlaying, setIsClassroomIntroPlaying] = useState(false);
  // True while a character question audio clip is playing in a sub-scene
  const isCutscenePlaying = !!selectedQuestion;
  const isMusicVideoOpen = modalContent?.type === "musicVideo";
  const isCreditsOpen = modalContent?.type === "credits";

  const CREDITS_BGM_SRC =
    "/assets/audio/bgm/CREDITS SB BGM9 ending scene_Edit for looping.mp3";
  const CREDITS_BGM_VOLUME = 0.35;
  // BGM is suppressed during the intro, classroom intro, or any cutscene
  const isBgmSuppressed =
    isIntroPlaying || isClassroomIntroPlaying || isCutscenePlaying;

  // True while the camera is lerping between positions (e.g., Classroom intro)
  const [isCameraLerping, setIsCameraLerping] = useState(false);

  useEffect(() => {
    // When entering Classroom, ensure isCameraLerping is true until Scene triggers lerp end
    if (scene === "Classroom") {
      setIsCameraLerping(true);
    }
  }, [scene]);

  // Show Stats Panel
  const [enableDevTools, setEnableDevTools] = useState<boolean>(false);
  const [pressedKeys, setPressedKeys] = useState<string[]>([]);
  const [showAnswerBox, setShowAnswerBox] = useState(false);

  const handleIntroComplete = useCallback(() => {
    setIsIntroPlaying(false);
  }, []);

  const skipIntroRef = useRef<(() => void) | null>(null);
  const replayIntroRef = useRef<(() => void) | null>(null);

  const classroomSkipIntroRef = useRef<(() => void) | null>(null);
  const classroomReplayIntroRef = useRef<(() => void) | null>(null);

  const handleClassroomIntroStart = useCallback(() => {
    setIsClassroomIntroPlaying(true);
  }, []);

  const handleClassroomIntroEnd = useCallback(() => {
    setIsClassroomIntroPlaying(false);
  }, []);

  const handleReplayIntro = useCallback(() => {
    setIsIntroPlaying(true);
    replayIntroRef.current?.();
  }, []);

  const [languageSelected, setLanguageSelected] = useState(false);

  const handleLanguageSelect = useCallback((lang: "en" | "fr") => {
    i18n.changeLanguage(lang);
    hasUserInteractedRef.current = true;
    setLanguageSelected(true);
  }, []);

  const getCurrentVisibleParagraph = () => {
    if (!selectedQuestion || visibleParagraphCount <= 0) return "";
    const segments = getTimedAnswerSegments(selectedQuestion, lang);
    const text = segments[visibleParagraphCount - 1] || "";
    return text ? preventOrphan(text) : "";
  };

  // Show answer paragraphs progressively using optional paragraph timestamps.
  // Falls back to answerTimestamp for backward compatibility.
  useEffect(() => {
    const timerIds: NodeJS.Timeout[] = [];

    if (selectedQuestion) {
      if (hasTimedParagraphs(selectedQuestion)) {
        const timedSegments = getTimedAnswerSegments(selectedQuestion, lang);
        const paragraphTimestamps = getParagraphTimestamps(selectedQuestion);

        timedSegments.forEach((_, index) => {
          const startAt = paragraphTimestamps[index];

          if (typeof startAt !== "number") {
            return;
          }

          const timerId = setTimeout(() => {
            setShowAnswerBox(true);
            setVisibleParagraphCount((prev) => Math.max(prev, index + 1));
          }, startAt * 1000);

          timerIds.push(timerId);
        });
      } else if (typeof selectedQuestion.answerTimestamp === "number") {
        const timerId = setTimeout(() => {
          setShowAnswerBox(true);
        }, selectedQuestion.answerTimestamp * 1000);

        timerIds.push(timerId);
      } else {
        setShowAnswerBox(true);
      }
    }

    // Clean up all timers when question or language changes
    return () => {
      timerIds.forEach((timerId) => clearTimeout(timerId));
      setShowAnswerBox(false);
      setVisibleParagraphCount(0);
    };
  }, [selectedQuestion, lang]);

  const keyPressed = useCallback((e: KeyboardEvent) => {
    setPressedKeys((prevPressedKeys) => {
      const newPressedKeys = [...prevPressedKeys, e.key];
      const cmdString = newPressedKeys.join("");

      const keyBinds = {
        "[][]": () => setEnableDevTools((prev) => !prev),
      };

      function escapeRegExp(input: string) {
        return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }

      for (const key of Object.keys(keyBinds)) {
        if (RegExp(escapeRegExp(key)).test(cmdString)) {
          keyBinds[key as keyof typeof keyBinds]();
          return [];
        }
      }

      return newPressedKeys;
    });
  }, []);

  // Handle key down
  useEffect(() => {
    document.addEventListener("keydown", keyPressed);
    return () => {
      document.removeEventListener("keydown", keyPressed);
    };
  }, [keyPressed]);

  function calculateDimensions() {
    return window.innerWidth > window.innerHeight * (16 / 9)
      ? { width: window.innerHeight * (16 / 9), height: window.innerHeight }
      : { width: window.innerWidth, height: window.innerWidth * (9 / 16) };
  }

  const [dimensions, setDimensions] = useState(calculateDimensions());

  const defaultBgm: BgmConfig = {
    src: "/assets/audio/bgm/OPENING PAGE SB pizzicato and synth-mix 2.mp3",
    volume: 0.3,
  };

  // Background music per scene (using files from public/ to avoid bundling)
  const getBgmForScene = (sceneName: string | null | undefined): BgmConfig => {
    if (!sceneName) {
      return defaultBgm;
    }

    switch (sceneName) {
      case "HearingRoom":
        return {
          src: "/assets/audio/bgm/HEARING ROOM SB twinklings wurli ob_Edit for looping.mp3",
          volume: 0.35,
        };
      case "HeartGarden":
        return {
          src: "/assets/audio/bgm/HEART GARDEN SB BGM9 ending scene_for looping REVISED.mp3",
          volume: 0.2,
        };
      case "LakeBearbine":
        return {
          src: "/assets/audio/bgm/LAKE BEARBINE Spirit Bear BGM 15 morning in the woods_edit for looping.mp3",
          volume: 0.5,
        };
      case "ParliamentHill":
        return {
          src: "/assets/audio/bgm/PARLIAMENT HILL SB fishing fun wurliedit_Edit for looping.mp3",
          volume: 0.15,
        };
      case "Classroom":
        return {
          src: "/assets/audio/bgm/CLASSROOM SB twinklings wurli delay zen.mp3",
          volume: 0.7,
        };
      default:
        return defaultBgm;
    }
  };

  const bgm = getBgmForScene(scene);
  const [devVolume, setDevVolume] = useState<number>(bgm.volume ?? 0.3);

  useEffect(() => {
    setDevVolume(bgm.volume ?? 0.3);
  }, [bgm.volume]);

  useEffect(() => {
    function handleResize() {
      setDimensions(calculateDimensions());
    }

    window.addEventListener("resize", handleResize);

    // Clean up event listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleBackButtonUp = () => {
    if (selectedQuestion) {
      setSelectedQuestion(null);
    } else if (isQuizMode) {
      setIsQuizMode(false);
    } else {
      setScene("");
      setSelectedQuestion(null);
      setIsQuizMode(false);
      setSceneLoaded(false);
      setShowAnswerBox(false);
      // Force a re-render of the Main component by clearing any cached state
      setSuspenseLoading(true);
      setTimeout(() => setSuspenseLoading(false), 0);
    }
  };

  const handleQuizButton = () => {
    if (!quizCompletions[scene]) {
      setIsQuizMode(!isQuizMode);
    }
  };

  const requiredQuizScenes = [
    "HeartGarden",
    "HearingRoom",
    "LakeBearbine",
    "ParliamentHill",
  ];
  const fullSceneNameMap: Record<string, { en: string; fr?: string }> = {
    LakeBearbine: { en: "Lake Bearbine", fr: "Lac Bearbine" },
    HearingRoom: {
      en: "Tribunal Hearing Room",
      fr: "Salle d'audience du tribunal",
    },
    HeartGarden: { en: "Heart Garden", fr: "Jardin du cœur" },
    ParliamentHill: { en: "Parliament Hill", fr: "Colline du Parlement" },
  };

  const hasAllQuizzesCompleted = requiredQuizScenes.every(
    (sceneName) => quizCompletions[sceneName] === true,
  );

  const openPawsport = () => {
    if (scene !== "Classroom") {
      setModalContent({ type: "pawsport" });
      return;
    }

    if (!hasAllQuizzesCompleted) {
      setModalContent({ type: "quizNotCompleted" });
      return;
    }

    setModalContent({ type: "pawsportInstruction" });
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "fr" : "en");
  };

  // useEffect(() => {
  //   const handlePageShow = (event: PageTransitionEvent) => {
  //     if (!event.persisted) return;

  //     // Reset transient UI/session state
  //     setSelectedQuestion(null);
  //     setIsQuizMode(false);
  //     setModalContent(null);
  //     setShowAnswerBox(false);
  //     setSceneLoaded(false);

  //     // Choose one policy:
  //     // 1) Clear quiz progress too:
  //     setQuizCompletions({});
  //     // 2) Keep quiz progress (if you persist it), only reset transient UI.
  //   };

  //   window.addEventListener("pageshow", handlePageShow);
  //   return () => window.removeEventListener("pageshow", handlePageShow);
  // }, []);

  return (
    <div className="App">
      <div id="landscape-only">
        <h2>This Application is best used in a landscape mode.</h2>
      </div>

      {/* Background Music */}
      {/* <BackgroundMusic ref={bgmRef} src={bgm.src} volume={devVolume} /> */}
      <BackgroundMusic
        ref={bgmRef}
        src={isCreditsOpen ? CREDITS_BGM_SRC : bgm.src}
        startTime={isCreditsOpen ? 12.5 : 0}
        volume={
          isCreditsOpen
            ? CREDITS_BGM_VOLUME
            : isMusicVideoOpen
              ? 0
              : isBgmSuppressed || isQuizNarrationPlaying
                ? devVolume * 0.4
                : devVolume
        }
      />

      <Suspense fallback={<Spinner />}>
        <div
          className="CanvasContainer"
          style={{ width: dimensions.width, height: dimensions.height }}
        >
          <Canvas
            dpr={[1, 1.5]}
            performance={{ min: 0.5 }}
            onCreated={({ gl }) => {
              gl.shadowMap.enabled = false; // Disable shadows
              gl.toneMappingExposure = 1;
              gl.toneMapping = THREE.NoToneMapping;
              gl.outputColorSpace = THREE.SRGBColorSpace;
            }}
            camera={{ near: 0.1, far: 100 }}
          >
            <ambientLight intensity={Math.PI / 1.5} />
            {/* NOTE: Eventually, change this part to turn off the EffectComposer when the framerate is slow (something like 24fps). */}
            {/* <EffectComposer enableNormalPass={false} multisampling={8}>
              <N8AO
                aoRadius={20}
                distanceFalloff={0.2}
                intensity={6}
                screenSpaceRadius
                halfRes
              />
            </EffectComposer> */}

            {scene ? (
              <ErrorBoundary
                key={scene}
                sceneName={scene}
                onReset={() => setScene("")}
              >
                <Scene
                  // @ts-ignore
                  data={data[scene]}
                  selectedQuestion={selectedQuestion}
                  setSelectedQuestion={setSelectedQuestion}
                  sceneName={scene}
                  onSceneLoaded={() => setSceneLoaded(true)}
                  isQuizMode={isQuizMode}
                  setIsQuizMode={setIsQuizMode}
                  setModalContent={setModalContent}
                  quizCompletions={quizCompletions}
                  setQuizCompletions={setQuizCompletions}
                  onIntroStart={
                    scene === "Classroom"
                      ? handleClassroomIntroStart
                      : undefined
                  }
                  onIntroEnd={
                    scene === "Classroom" ? handleClassroomIntroEnd : undefined
                  }
                  skipIntroRef={
                    scene === "Classroom" ? classroomSkipIntroRef : undefined
                  }
                  replayIntroRef={
                    scene === "Classroom" ? classroomReplayIntroRef : undefined
                  }
                  onCameraLerpChange={
                    scene === "Classroom" ? setIsCameraLerping : undefined
                  }
                  isCameraLerping={isCameraLerping}
                />
              </ErrorBoundary>
            ) : (
              <Main
                setScene={setScene}
                setSuspenseLoading={setSuspenseLoading}
                onIntroComplete={handleIntroComplete}
                skipIntroRef={skipIntroRef}
                replayIntroRef={replayIntroRef}
                startIntro={languageSelected}
              />
            )}

            {/* Debugging */}
            {enableDevTools && (
              <>
                <StatsGl />
                <OrbitControls target={[0, 0, 0]} zoomSpeed={0.65} />
                <Html>
                  <p className="change-log">Changed: 2:51 PT on May 05, 2026</p>
                </Html>
                <AudioDevPanel
                  audioRef={bgmRef}
                  volume={devVolume}
                  onVolumeChange={setDevVolume}
                />
              </>
            )}
          </Canvas>

          {!suspenseLoading &&
            !scene &&
            !(isIntroPlaying && languageSelected) && (
              <div className="Title">
                <img src={TitleImg} alt="title" />
              </div>
            )}

          {isIntroPlaying && languageSelected && (
            <button
              className="SkipButton"
              onClick={() => skipIntroRef.current?.()}
            >
              <img src={SkipBtnImg} alt="skip intro" />
              <p>{t("skip_intro")}</p>
            </button>
          )}

          {scene === "Classroom" &&
            sceneLoaded &&
            languageSelected &&
            !isCameraLerping && (
              <button
                className="SkipButton"
                onClick={() => {
                  if (isClassroomIntroPlaying)
                    classroomSkipIntroRef.current?.();
                  else classroomReplayIntroRef.current?.();
                }}
              >
                <img
                  src={SkipBtnImg}
                  alt={isClassroomIntroPlaying ? "skip intro" : "replay intro"}
                />
                <p>
                  {t(isClassroomIntroPlaying ? "skip_intro" : "replay_intro")}
                </p>
              </button>
            )}

          {!suspenseLoading && !languageSelected && (
            <div className="StartModal">
              <div className="StartModal__card">
                <p className="StartModal__title">
                  Choose your captions / Sélectionner la langue de sous-titrage
                </p>
                <div className="StartModal__buttons">
                  <button
                    className="StartModal__button"
                    onClick={() => handleLanguageSelect("en")}
                  >
                    English
                  </button>
                  <button
                    className="StartModal__button"
                    onClick={() => handleLanguageSelect("fr")}
                  >
                    Français
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Left Element (Back Button) */}
          {scene && sceneLoaded && !isClassroomIntroPlaying && (
            <div
              className="BackButton"
              onMouseUp={handleBackButtonUp}
              onTouchEnd={handleBackButtonUp}
            >
              <img
                src={BackBtnImg}
                alt="go back"
                style={{
                  display:
                    !initialScene && !isClassroomIntroPlaying
                      ? "block"
                      : initialScene && selectedQuestion
                        ? "block"
                        : "none",
                }}
              />
            </div>
          )}

          {/* Display answerText if a question is selected */}
          {showAnswerBox && selectedQuestion && (
            <div className="AnswerBox">
              {hasTimedParagraphs(selectedQuestion) ? (
                getCurrentVisibleParagraph() && (
                  <p
                    key={`${selectedQuestion.questionText_en}-${visibleParagraphCount}-${lang}`}
                    className="AnswerParagraphTransition"
                  >
                    {getCurrentVisibleParagraph()}
                  </p>
                )
              ) : (
                <p
                  key={`${selectedQuestion.questionText_en}-${lang}`}
                  dangerouslySetInnerHTML={{
                    __html: preventOrphan(
                      getAnswerText(selectedQuestion, lang),
                    ),
                  }}
                />
              )}
            </div>
          )}

          {/* Show Intro Subtitle when intro is playing (not before language is selected) */}
          <IntroSubtitle isIntroPlaying={isIntroPlaying && languageSelected} />

          {/* Show Classroom Subtitle when Classroom character intro is playing */}
          <IntroSubtitle
            translationKey="classroom_subtitle"
            isIntroPlaying={isClassroomIntroPlaying && languageSelected}
          />

          {/* Bottom Right Element (Quiz/Credits, Passport, Language Buttons) */}
          {!suspenseLoading &&
            !isIntroPlaying &&
            !showAnswerBox &&
            !selectedQuestion &&
            !isClassroomIntroPlaying &&
            (!scene || sceneLoaded) && (
              <div className="BottomContainerRight">
                {scene &&
                  sceneLoaded &&
                  !initialScene &&
                  !selectedQuestion &&
                  scene !== "Classroom" && (
                    <div
                      className="QuizButton"
                      onMouseUp={handleQuizButton}
                      onTouchEnd={handleQuizButton}
                      style={
                        quizCompletions[scene]
                          ? { cursor: "default" }
                          : { cursor: "pointer" }
                      }
                    >
                      <img
                        src={QuizBtnImg}
                        alt="quiz"
                        style={
                          quizCompletions[scene]
                            ? { filter: "grayscale(100%)" }
                            : { filter: "grayscale(0%)" }
                        }
                      />
                    </div>
                  )}

                {scene &&
                  sceneLoaded &&
                  !initialScene &&
                  !selectedQuestion &&
                  scene === "Classroom" && (
                    <div
                      className="CreditsButton"
                      onMouseUp={() => setModalContent({ type: "credits" })}
                      onTouchEnd={() => setModalContent({ type: "credits" })}
                    >
                      <img src={CreditsBtnImg} alt="credits" />
                    </div>
                  )}

                <div
                  className={
                    "PassportButton " +
                    (scene === "Classroom" ? " Classroom" : "") +
                    (scene === "Classroom" && hasAllQuizzesCompleted
                      ? " Completed"
                      : "")
                  }
                >
                  <img
                    src={
                      lang.startsWith("en")
                        ? PassportBtnImgEn
                        : PassportBtnImgFr
                    }
                    alt="pawsport"
                    onMouseUp={openPawsport}
                    onTouchEnd={openPawsport}
                  />
                </div>
                <div className="LanguageButton">
                  <img
                    src={LanguageBtnImg}
                    alt="change language"
                    onMouseUp={toggleLanguage}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      toggleLanguage();
                    }}
                  />
                </div>
              </div>
            )}

          {isQuizMode && !selectedQuestion && (
            <Quiz
              isQuizMode={isQuizMode}
              setIsQuizMode={setIsQuizMode}
              scene={scene}
              setQuizCompletions={setQuizCompletions}
              onNarrationChange={setIsQuizNarrationPlaying}
            />
          )}

          {/* Spirit Bear Speech Bubble */}
          {!suspenseLoading && !isIntroPlaying && !scene && (
            <>
              <div
                className="SpeechBubbleBox"
                style={{
                  top: `${dimensions.height / 2.6}px`,
                  left: `${dimensions.width / 1.59}px`,
                }}
              >
                <div
                  className="SpeechBubble"
                  style={{ cursor: "pointer" }}
                  onMouseUp={handleReplayIntro}
                  onTouchEnd={handleReplayIntro}
                >
                  <div>
                    <p>{t("replay_intro")}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {modalContent?.type === "browserBackWarning" && (
            <div className="StartModal">
              <div className="StartModal__card Instruction">
                <p className="StartModal__title">
                  {t("browser_back_instruction")}
                </p>
                <div className="StartModal__buttons">
                  <button
                    className="StartModal__button"
                    onClick={() => setModalContent(null)}
                  >
                    {t("close_instruction")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Shows resources in the Classroom */}
          {modalContent?.type === "resource" && (
            <ResourceModal
              modalContent={modalContent}
              onClose={() => setModalContent(null)}
            />
          )}

          {/* Shows a calendar in the Classroom */}
          {modalContent?.type === "calendar" && (
            <CalendarModal onClose={() => setModalContent(null)} />
          )}

          {/* Shows a scrapbook in the Classroom */}
          {modalContent?.type === "scrapbook" && (
            <ScrapbookModal onClose={() => setModalContent(null)} />
          )}

          {/* Shows the Pawsport */}
          {modalContent?.type === "pawsport" && (
            <PawsportModal
              onClose={() => setModalContent(null)}
              quizCompletions={quizCompletions}
            />
          )}

          {/* Shows the Quiz Not Completed instruction */}
          {modalContent?.type === "quizNotCompleted" && (
            <div className="StartModal">
              <div className="StartModal__card Instruction">
                <p className="StartModal__title">{t("complete_quiz")}</p>
                <strong className="StartModal__title">
                  {requiredQuizScenes
                    .filter((sceneName) => !quizCompletions[sceneName])
                    .map(
                      (sceneName) => fullSceneNameMap[sceneName][i18n.language],
                    )
                    .join(", ")}
                </strong>
                <div className="StartModal__buttons">
                  <button
                    className="StartModal__button"
                    onClick={() => setModalContent(null)}
                  >
                    {t("close_instruction")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Shows the Pawsport download instruction */}
          {modalContent?.type === "pawsportInstruction" && (
            <div className="StartModal">
              <div className="StartModal__card Instruction">
                <p className="StartModal__title">{t("pawsport_instruction")}</p>
                <div className="StartModal__buttons">
                  <button
                    className="StartModal__button"
                    onClick={() =>
                      window.open(
                        `/assets/pdf-passport/BearShares_PDF_pawsport_${lang}.pdf`,
                        "_blank",
                      )
                    }
                  >
                    {t("download_pawsport")}
                  </button>
                  <button
                    className="StartModal__button"
                    onClick={() => setModalContent(null)}
                  >
                    {t("close_instruction")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* <button
            style={{ position: "absolute", top: 20, left: 20, zIndex: 1000 }}
            onClick={() => setModalContent({ type: "pawsportInstruction" })}
          >
            finish quiz
          </button> */}

          {/* Shows the Music Video */}
          {modalContent?.type === "musicVideo" && (
            <MusicVideoModal onClose={() => setModalContent(null)} />
          )}

          {/* Shows the Credits */}
          {modalContent?.type === "credits" && (
            <CreditsModal onClose={() => setModalContent(null)} />
          )}

          {/* Overlay that prevents any flash of uninitialized scene content.
              Stays visible from when a scene is selected until onSceneLoaded fires,
              covering the frames where Three.js renders before scene setup effects run. */}
          {scene && !sceneLoaded && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 50,
                pointerEvents: "none",
              }}
            >
              <Spinner />
            </div>
          )}
        </div>
      </Suspense>
    </div>
  );
}

export default App;
